import { supabase } from './supabase';
import { apiRequest } from '../config/api';

export interface Configuracao {
  id: string;
  chave: string;
  valor: string;
  descricao: string;
  filial?: string;
}

export interface MotivoOcorrencia {
  id: string;
  nome: string;
  descricao: string;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

export interface Conferente {
  id: string;
  nome: string;
  filial: string;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

export const ConfiguracoesService = {
  // Configurações Gerais
  async buscarTodas(filial?: string): Promise<Configuracao[]> {
    const params = new URLSearchParams();
    if (filial) params.append('filial', filial);

    const response = await apiRequest(`/configuracoes?${params}`);
    return response.data || [];
  },

  async buscarPorChave(chave: string, filial?: string): Promise<Configuracao | null> {
    let query = supabase
      .from('configuracoes')
      .select('*')
      .eq('chave', chave);

    if (filial) {
      query = query.eq('filial', filial);
    }

    const { data, error } = await query.single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    return data;
  },

  async atualizar(chave: string, valor: string, filial?: string): Promise<void> {
    let query = supabase
      .from('configuracoes')
      .update({
        valor
      })
      .eq('chave', chave);

    if (filial) {
      query = query.eq('filial', filial);
    }

    const { error } = await query;

    if (error) throw error;
  },

  // Motivos de Ocorrência
  async buscarMotivosOcorrencia(): Promise<MotivoOcorrencia[]> {
    const response = await apiRequest('/motivos-ocorrencia?ativo=true');
    return response.data || [];
  },

  async criarMotivoOcorrencia(nome: string, descricao: string): Promise<MotivoOcorrencia> {
    const { data, error } = await supabase
      .from('motivos_ocorrencia')
      .insert({
        nome,
        descricao,
        ativo: true,
        criado_em: new Date().toISOString(),
        atualizado_em: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async atualizarMotivoOcorrencia(id: string, nome: string, descricao: string, ativo: boolean): Promise<void> {
    const { error } = await supabase
      .from('motivos_ocorrencia')
      .update({
        nome,
        descricao,
        ativo,
        atualizado_em: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  },

  async removerMotivoOcorrencia(id: string): Promise<void> {
    const { error } = await supabase
      .from('motivos_ocorrencia')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // Conferentes
  async buscarConferentes(): Promise<Conferente[]> {
    const response = await apiRequest('/conferentes');
    return response.data || [];
  },

  async criarConferente(nome: string, filial: string): Promise<Conferente> {
    const response = await apiRequest('/conferentes', {
      method: 'POST',
      body: JSON.stringify({
        nome,
        filiais: [filial], // Backend espera array de filiais
        ativo: true
      })
    });
    return response.data;
  },

  async atualizarConferente(id: string, nome: string, filial: string, ativo: boolean): Promise<void> {
    await apiRequest(`/conferentes/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        nome,
        filiais: [filial], // Backend espera array de filiais
        ativo
      })
    });
  },

  async removerConferente(id: string): Promise<void> {
    await apiRequest(`/conferentes/${id}`, {
      method: 'DELETE'
    });
  },
};
